import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { AlertTriangle } from "lucide-react";

function AuthConfigError() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F3F3F3] px-4">
      <div className="bg-white border border-[#E5E5E5] rounded-xl p-8 max-w-sm w-full text-center shadow-sm">
        <div className="w-10 h-10 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-5 h-5 text-rose-500" />
        </div>
        <h2 className="text-base font-semibold text-[#181818] mb-2">Authentication Unavailable</h2>
        <p className="text-sm text-[#706E6B]">
          The authentication configuration could not be loaded. Please refresh the page or contact your system administrator.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-5 w-full px-4 py-2.5 bg-[#0176D3] text-white text-sm font-medium rounded-lg hover:bg-[#014486] transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

export default function ProtectedRoute({ children }) {
  const { user, uatConfig } = useAuth();
  const brand = useBrand();

  // Wait for BOTH session check AND auth config to resolve — never render legacy login while resolving
  if (user === undefined || uatConfig === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F3F3F3]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#0176D3] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#706E6B] text-sm font-mono">
            Initializing {brand?.productShortName || "ONE Engage"}…
          </p>
        </div>
      </div>
    );
  }

  // Auth config failed — show error, never guess auth mode
  if (uatConfig?.error) {
    return <AuthConfigError />;
  }

  // Not authenticated — redirect to the correct login experience
  if (!user) {
    const loginPath = uatConfig?.enabled ? "/uat-login" : "/login";
    const qs = window.location.search;
    return <Navigate to={`${loginPath}${qs}`} replace />;
  }

  return children;
}
